import styled from "@emotion/styled";
import React, { Component, RefObject } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import Print from "src/components/icons/Print";
import VinNumber from "src/components/VinNumber/VinNumber";
import withFrame, { WithFrameProps } from "src/providers/frame/withFrame";
import { getAccountCountry } from "src/store/selectors/account";
import { getDealerOfferByVin } from "src/store/selectors/dealerOffer";
import { getTradeInValue } from "src/store/selectors/tradeIn";
import {
    getOfferDisclaimer,
    getStrikeThroughMsrp,
    getVehicle,
    getVehiclePrice,
    showMsrp,
} from "src/store/selectors/vehicles";
import { formatNumberAsPercentage, getPaymentFrequencyText, localizeCurrencyWithDecimals } from "src/utils";
import { isUS } from "src/utils/account";
import { openPrintDialog } from "src/utils/printDialog";
import { getOfferData, getPaymentData, getSelectedPaymentType } from "src/utils/vehicles";
import { DealerLeaseOffer } from "Types/api/DealerLeaseOffer";
import { DealerLoanOffer } from "Types/api/DealerLoanOffer";
import { DealerOffer } from "Types/api/DealerOffer";
import { DealerOfferTradein } from "Types/api/DealerOfferTradein";
import { LeasePayment } from "Types/api/LeasePayment";
import { LoanPayment } from "Types/api/LoanPayment";
import { Tax } from "Types/api/Tax";
import { ApiVehicle } from "Types/api/Vehicle";
import {
    AccountState,
    ApplicationStore,
    DealState,
    LeaseOffer,
    LoanOffer,
    Offer,
    Payment,
    VehicleState,
} from "Types/state";
import { TradeIn } from "Types/TradeIn";
import { ModalComponentProps } from "../..";
import List, { ListItem, ListProps } from "./List";
import "./PaymentPlanReviewModal.scss";

const TRANSLATION_KEY = "Modal.PaymentPlanReviewModal";

const PrintIcon = styled(Print)`
    cursor: pointer;
    position: absolute;
    right: 24px;
    top: 80px;
    width: 26px;
    height: 24px;

    ${(props) => props.theme.breakpoints.up("md")} {
        right: 41px;
        top: 40px;
        width: 30px;
        height: 28px;
    }

    @media print {
        display: none;
    }
`;

export const MSRPWithPrice = styled("span")<{ showStrikethrough: boolean }>`
    text-decoration: ${(props) => (props.showStrikethrough ? "line-through" : "none")};
`;

export interface PaymentPlanReviewModalProps extends ModalComponentProps {
    account: AccountState;
    deal: DealState;
    dealerOffer?: DealerOffer;
    disclaimerText: string;
    region?: string;
    tradeIn: TradeIn;
    vehicle: VehicleState;
    vehiclePrice: number;
    offerIndex?: number;
    showStrikethroughForMSRP: boolean;
    canShowMsrp: boolean;
}

interface PaymentPlanReviewModalState {
    isCollapsedFees: boolean;
    isCollapsedFinancing: boolean;
    isCollapsedIncentives: boolean;
    isCollapsedTaxes: boolean;
    isCollapsedTradeIn: boolean;
    isCollapsedUpgrades: boolean;
    isCollapsedAncillaryProducts: boolean;
    isCollapsedVehiclePrice: boolean;
}

class PaymentPlanReviewModal extends Component<
    PaymentPlanReviewModalProps & WithTranslation & WithFrameProps,
    PaymentPlanReviewModalState
> {
    private readonly componentRef: RefObject<HTMLElement>;

    public constructor(props: PaymentPlanReviewModalProps & WithTranslation & WithFrameProps) {
        super(props);
        this.componentRef = React.createRef<HTMLElement>();

        this.state = {
            isCollapsedFees: true,
            isCollapsedFinancing: true,
            isCollapsedIncentives: true,
            isCollapsedTaxes: true,
            isCollapsedTradeIn: true,
            isCollapsedUpgrades: true,
            isCollapsedAncillaryProducts: true,
            isCollapsedVehiclePrice: true,
        };
    }

    public render() {
        const { vehicle } = this.props;

        return vehicle ? (
            <>
                <span onClick={this.triggerPrint}>
                    <PrintIcon />
                </span>
                <section className="PaymentPlanReviewModal" ref={this.componentRef}>
                    {this.renderHeader()}
                    <div className="PaymentPlanReviewModal__main">
                        <ul className="PaymentPlanReviewModal__groups">{this.renderLists()}</ul>
                    </div>
                    {this.renderFooter()}
                </section>
            </>
        ) : null;
    }

    private renderHeader() {
        const { deal, vehicle } = this.props;

        return (
            <header className="PaymentPlanReviewModal__header">
                <div className="PaymentPlanReviewModal__image-container">
                    <img className="PaymentPlanReviewModal__image" src={vehicle.thumbnail} alt="" />
                </div>
                <div className="PaymentPlanReviewModal__details-container">
                    <p className="PaymentPlanReviewModal__details">
                        <b>{deal.person && deal.person.first_name !== undefined && `${deal.person.first_name}'s `}</b>
                        {vehicle.title}
                        <br />
                        <VinNumber>{vehicle.vin}</VinNumber>
                    </p>
                    {this.renderVehicleColorInfo()}
                </div>
            </header>
        );
    }

    private renderLists() {
        const { account, tradeIn, vehicle, offerIndex, dealerOffer } = this.props;
        const payment = dealerOffer || (getPaymentData(vehicle) as Payment);
        const offer = dealerOffer || this.getBankOfferData(offerIndex, payment, vehicle);
        const tradeInfo = dealerOffer ? dealerOffer.tradein : tradeIn;

        const listsProps: ListProps[] = [];
        const groups = [
            () => this.getVehicleInfo(vehicle),
            () => this.getTradeIn(tradeInfo),
            () => this.getIncentives(offer),
            () => this.getUpgrades(vehicle, account), // dealer.accessories/warranties
            () => this.getFees(offer),
            () => this.getAncillaryProducts(vehicle),
            () => this.getTaxes(offer, account),
        ];

        const selectedPayment = getSelectedPaymentType(vehicle);

        if (selectedPayment !== "cash") {
            const financingPayment = payment as LeasePayment | LoanPayment | DealerLeaseOffer | DealerLoanOffer;
            groups.push(() => this.getFinancing(financingPayment, offer, selectedPayment, account));
        }

        groups.forEach((getGroupData) => {
            const groupdata = getGroupData();

            if (groupdata) {
                listsProps.push(groupdata);
            }
        });
        return listsProps.map((listProps, index) => {
            return (
                <li className="PaymentPlanReviewModal__group" key={index}>
                    <List {...listProps} />
                </li>
            );
        });
    }

    private renderFooter() {
        const { vehicle, disclaimerText, region, t, offerIndex, dealerOffer } = this.props;
        const selectedPaymentName = getSelectedPaymentType(vehicle);
        const payment = dealerOffer || (getPaymentData(vehicle) as Payment);
        const offer = dealerOffer || this.getBankOfferData(offerIndex, payment, vehicle);

        let paymentBreakdown;
        let paymentInfo;

        if (selectedPaymentName === "lease" && offer) {
            const leasePayment = payment as LeasePayment | DealerLeaseOffer;
            const leaseOffer = offer as LeaseOffer | DealerLeaseOffer;
            paymentBreakdown = `${leasePayment.term} ${t(`${TRANSLATION_KEY}.months`)} ${leasePayment.miles} ${t(
                isUS(region) ? `${TRANSLATION_KEY}.miles` : `${TRANSLATION_KEY}.kilometers`
            )}`;
            paymentInfo = (
                <>
                    <span className="CalculatedPayment__payment">
                        {localizeCurrencyWithDecimals(leaseOffer?.payment)}
                    </span>
                    <span className="CalculatedPayment__copy">{` ${getPaymentFrequencyText(
                        leaseOffer!.frequency
                    )}`}</span>
                </>
            );
        }
        if (selectedPaymentName === "loan") {
            const loanPayment = payment as LoanPayment | DealerLoanOffer;
            const loanOffer = offer as LoanOffer | DealerLoanOffer;
            paymentBreakdown = `${loanPayment.term} ${t(`${TRANSLATION_KEY}.months`)} ${
                loanOffer && formatNumberAsPercentage(loanOffer.interest_rate)
            }`;
            paymentInfo = (
                <>
                    <span className="CalculatedPayment__payment">
                        {localizeCurrencyWithDecimals(loanOffer?.payment)}
                    </span>
                    <span className="CalculatedPayment__copy">{` ${getPaymentFrequencyText(
                        loanOffer!.frequency
                    )}`}</span>
                </>
            );
        }

        return (
            <footer className="PaymentPlanReviewModal__footer">
                <div className="PaymentPlanReviewModal__footer-main-content">
                    {offer && <span className="APR__copy">{paymentBreakdown}</span>}
                    {offer && (
                        <div className="PaymentPlanReviewModal__price-container">
                            <div className="CalculatedPayment__paymentContainer">{paymentInfo}</div>
                        </div>
                    )}
                </div>
                <div className="PaymentPlanReviewModal__disclaimer-text">
                    <p>{disclaimerText}</p>
                </div>
            </footer>
        );
    }

    private renderVehicleColorInfo() {
        // TODO uncomment when vehicle color data be retrieved from API
        /* return (
            <ul className="PaymentPlanReviewModal__colors">
                <li className="PaymentPlanReviewModal__color">
                    <span className="PaymentPlanReviewModal__color-pin PaymentPlanReviewModal__color-pin--grey" />
                    <span className="PaymentPlanReviewModal__color-label">
                        Exterior: Silver Ice Metallic
                    </span>
                </li>
                <li className="PaymentPlanReviewModal__color">
                    <span className="PaymentPlanReviewModal__color-pin PaymentPlanReviewModal__color-pin--black" />
                    <span className="PaymentPlanReviewModal__color-label">
                        Interior: Black
                    </span>
                </li>
            </ul>
        ); */
        return "";
    }

    private triggerPrint = () => {
        this.setState(
            {
                isCollapsedFees: false,
                isCollapsedFinancing: false,
                isCollapsedIncentives: false,
                isCollapsedTaxes: false,
                isCollapsedTradeIn: false,
                isCollapsedUpgrades: false,
                isCollapsedAncillaryProducts: false,
                isCollapsedVehiclePrice: false,
            },
            () => openPrintDialog(this.componentRef && this.componentRef.current!, this.props.iframe.document)
        );
    };

    private getVehicleInfo = (vehicle: VehicleState) => {
        const { t, account, vehiclePrice } = this.props;
        const items: ListItem[] = [];
        let totalAmount;

        if (vehicle.msrp && this.props.canShowMsrp) {
            items.push({
                label: t(`${TRANSLATION_KEY}.msrpLabel`),
                value: (
                    <MSRPWithPrice
                        showStrikethrough={vehicle.msrp !== vehiclePrice && this.props.showStrikethroughForMSRP}
                    >
                        {localizeCurrencyWithDecimals(vehicle.msrp)}
                    </MSRPWithPrice>
                ),
            });
            totalAmount = vehicle.msrp;
        }

        if (!this.props.canShowMsrp || !vehicle.msrp || vehicle.msrp !== vehiclePrice) {
            items.push({
                label: account.account_disclaimers.base_price,
                value: localizeCurrencyWithDecimals(vehiclePrice),
            });
            totalAmount = vehiclePrice;
        }

        return {
            collapsed: this.state.isCollapsedVehiclePrice,
            e2eName: "vehicle-info-group",
            items,
            onClick: this.onClick("isCollapsedVehiclePrice"),
            title: `${vehicle.title}`,
            totalAmount,
        };
    };

    private getTradeIn = (tp: TradeIn | DealerOfferTradein) => {
        const { t } = this.props;

        const tradeInEstimate =
            (tp as DealerOfferTradein).cash_out ||
            (tp as DealerOfferTradein).allowance ||
            getTradeInValue({ tradeIn: tp as TradeIn } as ApplicationStore) ||
            0;

        if (!tradeInEstimate) {
            return null;
        } else {
            const items: ListItem[] = [];
            items.push({
                label: t(`${TRANSLATION_KEY}.estimatedTradeInLabel`),
                value: localizeCurrencyWithDecimals(tradeInEstimate * -1),
            });

            if ((tp.balance ?? 0) > 0) {
                items.push({
                    label: t(`${TRANSLATION_KEY}.estimatedTradeInAmount`),
                    value: localizeCurrencyWithDecimals(tp.balance),
                });
            }

            return {
                collapsed: this.state.isCollapsedTradeIn,
                collapsible: true,
                e2eName: "trade-in-group",
                items,
                onClick: this.onClick("isCollapsedTradeIn"),
                title: t(`${TRANSLATION_KEY}.tradeInTitle`),
                totalAmount: (tp.balance || 0) - tradeInEstimate,
            };
        }
    };

    private getIncentives = (offer: Offer | DealerOffer | null) => {
        const { t, account } = this.props;
        const rebates = (offer && offer.rebates) || [];
        const items: ListItem[] = [];
        let totalAmount = 0;

        rebates.forEach((rebate: any) => {
            items.push({
                label: rebate.name,
                value: localizeCurrencyWithDecimals(rebate.value * -1),
            });
            totalAmount -= rebate.value;
        });

        return items.length
            ? {
                  collapsed: this.state.isCollapsedIncentives,
                  e2eName: "incentives-group",
                  items,
                  onClick: this.onClick("isCollapsedIncentives"),
                  title: t(`${TRANSLATION_KEY}.incentivesTitle`),
                  tooltip:
                      (account && account.account_disclaimers && account.account_disclaimers.global_accuracy) || "",
                  totalAmount,
              }
            : null;
    };

    private getUpgrades = (vehicle: ApiVehicle, account: AccountState) => {
        const { dealerOffer } = this.props;
        // TODO update method when protection be retrieved by API
        const vehicleProtections = [] as any;
        const accessories = dealerOffer ? dealerOffer.accessories : vehicle.accessories || [];
        // const warranties = dealerOffer ? dealerOffer.warranties : []; // TODO update when protections received by API?
        const items: ListItem[] = [];
        let totalAmount = 0;

        if (!accessories.length) {
            return null;
        } else if (dealerOffer) {
            accessories.forEach((a: any) => {
                items.push({
                    label: a.name || "",
                    value: localizeCurrencyWithDecimals(a.value),
                });
                totalAmount += a.value || 0;
            });
        } else {
            accessories.forEach((a: any) => {
                items.push({
                    label: a.title || "",
                    value: localizeCurrencyWithDecimals(a.price),
                });
                totalAmount += a.price || 0;
            });
        }

        vehicleProtections.forEach((protection: any) => {
            items.push({
                label: protection.title,
                value: localizeCurrencyWithDecimals(protection.price),
            });
            totalAmount += protection.price || 0;
        });

        return items.length
            ? {
                  collapsed: this.state.isCollapsedUpgrades,
                  e2eName: "upgrades-group",
                  items,
                  onClick: this.onClick("isCollapsedUpgrades"),
                  title: account.account_disclaimers.upgrades_label,
                  totalAmount,
              }
            : null;
    };

    private getAncillaryProducts = (vehicle: ApiVehicle): ListProps | null => {
        const { t } = this.props;
        if (!vehicle.ancillary_products?.length) return null;

        const items = [] as ListItem[];

        let totalAmount = 0;
        vehicle.ancillary_products.forEach(({ name, options }) => {
            items.push({
                label: name,
                value: localizeCurrencyWithDecimals(options[0].totalPrice),
            });
            totalAmount += options[0].totalPrice ?? 0;
        });

        return {
            collapsed: this.state.isCollapsedAncillaryProducts,
            e2eName: "ancillary-products-group",
            items,
            onClick: this.onClick("isCollapsedAncillaryProducts"),
            title: t(`${TRANSLATION_KEY}.ancillaryProductsTitle`),
            totalAmount,
        };
    };

    private getFees = (offer: Offer | DealerOffer | null) => {
        if (!offer?.fees?.length) {
            return null;
        }

        const items = [] as ListItem[];

        let totalAmount = 0;
        offer.fees.forEach((fee) => {
            items.push({
                label: fee.name,
                value: localizeCurrencyWithDecimals(fee.value),
            });
            totalAmount += fee.value ?? 0;
        });

        return {
            collapsed: this.state.isCollapsedFees,
            e2eName: "fees-group",
            items,
            onClick: this.onClick("isCollapsedFees"),
            title: this.props.t(`${TRANSLATION_KEY}.feesTitle`),
            totalAmount,
        };
    };

    private getTaxes = (offer: Offer | DealerOffer | null, account: AccountState) => {
        const { t } = this.props;

        if (!offer?.taxes?.length) {
            return null;
        }

        const items = [] as ListItem[];

        // DealerOfferListItem has no 'collected' property, so will fall into notMonthlyTaxes
        const notMonthlyTaxes = offer.taxes.filter((tax) => (tax as Tax).collected !== "monthly");
        const monthlyTaxes = offer.taxes.filter((tax) => (tax as Tax).collected === "monthly");

        let totalAmount = 0;
        notMonthlyTaxes.forEach((tax) => {
            items.push({
                label: tax.name,
                value: localizeCurrencyWithDecimals(tax.value),
            });
            totalAmount += tax.value ?? 0;
        });

        if (monthlyTaxes.length) {
            let monthlyTaxesSummary = 0;
            monthlyTaxes.forEach((tax) => (monthlyTaxesSummary += tax.value ?? 0));
            items.unshift({
                label: t(`${TRANSLATION_KEY}.monthlySalesTaxLabel`),
                value: `${localizeCurrencyWithDecimals(monthlyTaxesSummary)}/${t(`${TRANSLATION_KEY}.mo`)}`,
            });
        }

        return {
            collapsed: this.state.isCollapsedTaxes,
            e2eName: "taxes-group",
            items,
            onClick: this.onClick("isCollapsedTaxes"),
            title: t(`${TRANSLATION_KEY}.taxesTitle`),
            tooltip: account?.account_disclaimers?.global_taxes || "",
            totalAmount,
        };
    };

    private getFinancing = (
        payment: LeasePayment | LoanPayment | DealerLeaseOffer | DealerLoanOffer | null,
        offer: Offer | DealerOffer | null,
        paymentType: string,
        account: AccountState
    ) => {
        const { t } = this.props;
        const isLease = paymentType === "lease";
        const items: ListItem[] = [];
        let totalAmount = 0;
        const downPayment = (payment && payment.down_payment) || 0;
        const amountFinanced = (offer && offer.amount_financed) || 0;
        if (isLease) {
            items.push({
                label:
                    account.account_disclaimers?.down_payment_label_lease ??
                    t(`${TRANSLATION_KEY}.leaseDownPaymentLabel`),
                value: localizeCurrencyWithDecimals(downPayment * -1),
            });
            totalAmount -= downPayment;
        } else {
            items.push({
                label: t(`${TRANSLATION_KEY}.totalLabel`),
                value: localizeCurrencyWithDecimals(amountFinanced + downPayment),
            });
            items.push({
                label:
                    account.account_disclaimers?.down_payment_label_loan ??
                    t(`${TRANSLATION_KEY}.othersDownPaymentLabel`),
                value: localizeCurrencyWithDecimals(downPayment * -1),
            });
            totalAmount = amountFinanced;
        }

        return {
            collapsed: this.state.isCollapsedFinancing,
            e2eName: "financing-group",
            items,
            onClick: this.onClick("isCollapsedFinancing"),
            title: isLease ? t(`${TRANSLATION_KEY}.leaseTitle`) : t(`${TRANSLATION_KEY}.financingTitle`),
            totalAmount,
        };
    };

    private onClick = (key: keyof PaymentPlanReviewModalState) => (collapsed: boolean) => {
        this.setState((prevState: PaymentPlanReviewModalState) => ({
            ...prevState,
            [key]: collapsed,
        }));
    };

    private getBankOfferData = (
        offerIndex: number | undefined,
        payment: Payment,
        vehicle: VehicleState
    ): Offer | null => {
        return offerIndex !== undefined && payment.offers && payment.offers[offerIndex]
            ? payment.offers[offerIndex]
            : getOfferData(vehicle);
    };
}

const IntlPaymentPlanReviewModal = withTranslation()(PaymentPlanReviewModal);

function mapStateToProps(state: ApplicationStore) {
    const vin = state.currentDeal.vin as string;

    return {
        account: state.account,
        deal: state.deal,
        dealerOffer: getDealerOfferByVin(state, vin),
        disclaimerText: getOfferDisclaimer(state, vin),
        region: getAccountCountry(state),
        tradeIn: state.tradeIn,
        vehicle: getVehicle(state, vin),
        vehiclePrice: getVehiclePrice(state, vin),
        offerIndex: state.onlineShopper.subModal.bankOfferId,
        showStrikethroughForMSRP: getStrikeThroughMsrp(state, vin),
        canShowMsrp: showMsrp(state, vin),
    };
}

export default connect(mapStateToProps)(withFrame(IntlPaymentPlanReviewModal));
